import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Sun,
  Moon,
  Cloud,
  CloudSun,
  CloudRain,
  CloudFog,
  CloudSnow,
  CloudLightning,
  Lightning,
  Drop,
  Leaf,
  MapPin,
  Wind,
} from "@phosphor-icons/react";
import { getAirQuality, getReportBundle } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { formatTemp } from "@/lib/units";

/* =========================================================================
 * Phase 61 (May 8 2026) — Apple-Weather Report Page V2.
 *
 *   • Single, vertical, scannable scroll (no Today/Now toggle).
 *   • Plain-English everywhere — no CAPE / LI / METAR / sounding tiles.
 *     The technical engine still computes them; they live on the Verdict
 *     page now in friendly form.
 *   • Pro-tier value-add tiles: AQI + Allergy Index, UV through the day
 *     (with night fallback), 30-day rainfall calendar + climatology
 *     verdict, 24h Lightning strikes, Soil moisture advice, "Cool-zone"
 *     travel suggestion. All driven from a single /api/report-bundle call.
 *   • LocationBar removed — location persists from Verdict page (Phase 61).
 * ===================================================================== */

const NUM = {
  fontFamily: "Manrope, system-ui, sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.04em",
  fontFeatureSettings: "'tnum' 1, 'cv11' 1",
};

const INK = {
  primary: "#1c1917",
  secondary: "#44403c",
  muted: "#78716c",
  micro: "#a8a29e",
  paperSoft: "rgba(253,250,242,0.78)",
  rule: "rgba(28,25,23,0.10)",
  ruleSoft: "rgba(28,25,23,0.05)",
};

const ACCENT = {
  hi: "#b91c1c",
  lo: "#1e40af",
  sun: "#d97706",
  moon: "#7c3aed",
  rain: "#1d4ed8",
  bolt: "#a855f7",
  soil: "#65a30d",
  cool: "#0d9488",
};

/* ---------------- helpers ---------------- */

function hoursFrom(iso) {
  if (!iso) return null;
  return new Date(iso).getHours();
}

function fmtTime(iso) {
  if (!iso) return "—";
  // Open-Meteo returns location-local time as "YYYY-MM-DDTHH:MM" with no offset.
  // Format directly from the string so the browser's TZ never gets a chance to drift it.
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mm} ${suffix}`;
}

/** Parse an Open-Meteo "YYYY-MM-DDTHH:MM" string (which is the *location's* local
 * time with no offset suffix) into a Date that represents the correct UTC instant.
 * Without this, Date(iso) silently parses against the *browser's* local TZ, which
 * is wrong for any user roaming or any test pod running in UTC. */
function parseLocalIso(iso, utcOffsetSeconds) {
  if (!iso) return null;
  if (utcOffsetSeconds == null) return new Date(iso);
  const sign = utcOffsetSeconds >= 0 ? "+" : "-";
  const abs = Math.abs(utcOffsetSeconds);
  const hh = String(Math.floor(abs / 3600)).padStart(2, "0");
  const mm = String(Math.floor((abs % 3600) / 60)).padStart(2, "0");
  return new Date(`${iso}${sign}${hh}:${mm}`);
}

function weatherCodeMeta(code) {
  if (code == null) return { Icon: Cloud, tone: "#94a3b8", word: "—" };
  if (code === 0) return { Icon: Sun, tone: "#f59e0b", word: "Clear" };
  if (code <= 2) return { Icon: CloudSun, tone: "#a3a3a3", word: "Partly cloudy" };
  if (code === 3) return { Icon: Cloud, tone: "#737373", word: "Overcast" };
  if (code <= 48) return { Icon: CloudFog, tone: "#a3a3a3", word: "Fog" };
  if (code <= 55) return { Icon: CloudRain, tone: "#0ea5e9", word: "Drizzle" };
  if (code <= 67) return { Icon: CloudRain, tone: "#0284c7", word: "Rain" };
  if (code <= 77) return { Icon: CloudSnow, tone: "#a5b4fc", word: "Snow" };
  if (code <= 82) return { Icon: CloudRain, tone: "#0369a1", word: "Showers" };
  if (code <= 86) return { Icon: CloudSnow, tone: "#a5b4fc", word: "Snow showers" };
  return { Icon: CloudLightning, tone: "#7c3aed", word: "Thunder" };
}

const UV_TONE = (uv) => {
  if (uv == null) return { color: "#71717a", label: "—" };
  if (uv < 3) return { color: "#16a34a", label: "Low" };
  if (uv < 6) return { color: "#ca8a04", label: "Moderate" };
  if (uv < 8) return { color: "#ea580c", label: "High" };
  if (uv < 11) return { color: "#dc2626", label: "Very high" };
  return { color: "#9333ea", label: "Extreme" };
};

/** Allergy index proxy from PM2.5 (Open-Meteo air-quality pollen is EU-only,
 * so we approximate from particulate load — works as an India-friendly hint). */
function allergyFromPm25(pm25) {
  if (pm25 == null) return null;
  if (pm25 < 12) return { level: "Low", color: "#16a34a", advice: "Allergens are low — comfortable for sensitive people." };
  if (pm25 < 35) return { level: "Moderate", color: "#ca8a04", advice: "Some sensitive people may notice irritation outdoors." };
  if (pm25 < 55) return { level: "High", color: "#ea580c", advice: "Asthma & allergy sufferers — consider a mask outdoors." };
  if (pm25 < 90) return { level: "Very High", color: "#dc2626", advice: "Limit prolonged outdoor exertion. Mask recommended." };
  return { level: "Extreme", color: "#9333ea", advice: "Avoid outdoor activity. Run an indoor purifier." };
}

/* ---------------- UI atoms ---------------- */

function Section({ title, accent, right, children, testId, delay = 0 }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, delay, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="rounded-[18px] overflow-hidden"
      style={{
        background: INK.paperSoft,
        border: `1px solid ${INK.rule}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow:
          "0 1px 0 rgba(255,255,255,0.6) inset, 0 10px 26px -16px rgba(20,12,4,0.10)",
      }}
    >
      <header
        className="flex items-center gap-2 px-4 pt-3 pb-2"
        style={{ borderBottom: `1px solid ${INK.ruleSoft}` }}
      >
        <span
          aria-hidden
          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: accent || INK.muted }}
        />
        <h3
          className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
          style={{ color: INK.secondary }}
        >
          {title}
        </h3>
        {right && <div className="ml-auto">{right}</div>}
      </header>
      <div className="px-4 pt-3 pb-4">{children}</div>
    </motion.section>
  );
}

function Pill({ color, children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2.5 py-[3px] rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.14em] whitespace-nowrap"
      style={{
        background: `${color}1f`,
        color,
        border: `1px solid ${color}66`,
      }}
    >
      {children}
    </span>
  );
}

/* ---------------- Day-arc temperature curve ---------------- */

function DayTempCurve({ hourly, current, sunrise, sunset, tempUnit = "C" }) {
  const series = useMemo(() => {
    const t = hourly?.time || [];
    const v = hourly?.temperature_2m || [];
    const c = hourly?.weather_code || [];
    const arr = [];
    const todayKey = new Date().toISOString().slice(0, 10);
    for (let i = 0; i < t.length; i++) {
      if (!t[i] || t[i].slice(0, 10) !== todayKey) continue;
      arr.push({ iso: t[i], temp: v[i], code: c[i], hour: hoursFrom(t[i]) });
    }
    return arr;
  }, [hourly]);

  if (series.length < 6) return null;

  const temps = series.map((s) => s.temp);
  const min = Math.min(...temps);
  const max = Math.max(...temps);
  const range = Math.max(1, max - min);
  const W = 320;
  const H = 110;
  const PAD_X = 14;
  const PAD_Y = 18;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;
  const step = innerW / (series.length - 1);
  const xy = series.map((s, i) => ({
    x: PAD_X + i * step,
    y: PAD_Y + (1 - (s.temp - min) / range) * innerH,
    ...s,
  }));
  const path = xy.reduce((acc, p, i) => {
    if (i === 0) return `M${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    const prev = xy[i - 1];
    const cx = (prev.x + p.x) / 2;
    return `${acc} C${cx.toFixed(1)},${prev.y.toFixed(1)} ${cx.toFixed(1)},${p.y.toFixed(1)} ${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  }, "");
  const area = `${path} L${xy[xy.length - 1].x.toFixed(1)},${H - 2} L${xy[0].x.toFixed(1)},${H - 2} Z`;

  const nowH = new Date().getHours();
  const nowIdx = xy.findIndex((p) => p.hour === nowH);
  const nowPt = nowIdx >= 0 ? xy[nowIdx] : null;
  const srH = sunrise ? new Date(sunrise).getHours() : 6;
  const ssH = sunset ? new Date(sunset).getHours() : 18;
  const dayStart = xy.find((p) => p.hour >= srH);
  const dayEnd = [...xy].reverse().find((p) => p.hour <= ssH);
  const tickHours = [6, 12, 18, 0];
  const ticks = tickHours.map((h) => xy.find((p) => p.hour === h)).filter(Boolean);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-[120px] block"
      data-testid="day-temp-curve"
    >
      <defs>
        <linearGradient id="dtc-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dc2626" stopOpacity="0.30" />
          <stop offset="55%" stopColor="#f97316" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#fef3c7" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="dtc-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1e3a8a" />
          <stop offset="50%" stopColor="#dc2626" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </linearGradient>
      </defs>
      {dayStart && dayEnd && (
        <rect
          x={dayStart.x}
          y={0}
          width={Math.max(2, dayEnd.x - dayStart.x)}
          height={H}
          fill="#fde68a"
          opacity="0.16"
        />
      )}
      <path d={area} fill="url(#dtc-area)" />
      <path d={path} fill="none" stroke="url(#dtc-line)" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      {ticks.map((t) => (
        <g key={t.iso}>
          <line x1={t.x} y1={H - PAD_Y + 4} x2={t.x} y2={H - 2} stroke={INK.rule} strokeWidth="1" />
          <text x={t.x} y={H - 4} textAnchor="middle" fontSize="8" fontWeight="700" fill={INK.muted}>
            {t.hour === 0 ? "12a" : t.hour === 12 ? "12p" : t.hour > 12 ? `${t.hour - 12}p` : `${t.hour}a`}
          </text>
        </g>
      ))}
      {(() => {
        const hiPt = xy.reduce((a, b) => (b.temp > a.temp ? b : a), xy[0]);
        const loPt = xy.reduce((a, b) => (b.temp < a.temp ? b : a), xy[0]);
        return (
          <g>
            <circle cx={hiPt.x} cy={hiPt.y} r="3" fill="#dc2626" stroke="#fdfaf2" strokeWidth="1.5" />
            <text x={hiPt.x} y={hiPt.y - 7} textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#b91c1c" style={NUM}>
              {formatTemp(hiPt.temp, tempUnit, { suffix: "" })}°
            </text>
            <circle cx={loPt.x} cy={loPt.y} r="3" fill="#1e40af" stroke="#fdfaf2" strokeWidth="1.5" />
            <text x={loPt.x} y={loPt.y + 12} textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#1e40af" style={NUM}>
              {formatTemp(loPt.temp, tempUnit, { suffix: "" })}°
            </text>
          </g>
        );
      })()}
      {nowPt && (
        <g>
          <line x1={nowPt.x} y1={PAD_Y - 6} x2={nowPt.x} y2={H - PAD_Y + 4} stroke="#1c1917" strokeWidth="1" strokeDasharray="2,2" opacity="0.42" />
          <circle cx={nowPt.x} cy={nowPt.y} r="4.5" fill="#fef3c7" stroke="#1c1917" strokeWidth="1.5" />
          <circle cx={nowPt.x} cy={nowPt.y} r="2" fill="#1c1917" />
          <text x={nowPt.x} y={PAD_Y - 8} textAnchor="middle" fontSize="8.5" fontWeight="800" fill={INK.primary} style={{ letterSpacing: "0.16em" }}>
            NOW {formatTemp(current?.temperature_2m ?? nowPt.temp, tempUnit, { suffix: "" })}°
          </text>
        </g>
      )}
    </svg>
  );
}

/* ---------------- 7-day strip ---------------- */

const WEEKDAY_FMT = new Intl.DateTimeFormat(undefined, { weekday: "short" });

function SevenDayStrip({ daily, tempUnit = "C" }) {
  const times = daily?.time || [];
  const tmax = daily?.temperature_2m_max || [];
  const tmin = daily?.temperature_2m_min || [];
  const probs = daily?.precipitation_probability_max || [];
  const codes = daily?.weather_code || [];
  const days = times.slice(0, 7).map((iso, i) => ({
    iso,
    label: i === 0 ? "Today" : WEEKDAY_FMT.format(new Date(iso)),
    max: tmax[i],
    min: tmin[i],
    prob: probs[i],
    code: codes[i],
  }));
  if (!days.length) return null;
  const allMax = Math.max(...days.map((d) => d.max ?? -Infinity));
  const allMin = Math.min(...days.map((d) => d.min ?? Infinity));

  return (
    <div className="grid grid-cols-7 gap-0.5" data-testid="report-7day-strip">
      {days.map((d) => {
        const meta = weatherCodeMeta(d.code);
        const span = allMax - allMin || 1;
        const barLeft = ((d.min - allMin) / span) * 100;
        const barWidth = ((d.max - d.min) / span) * 100;
        return (
          <div key={d.iso} className="text-center px-0.5" data-testid={`report-day-${d.iso?.slice(0, 10)}`}>
            <div className="text-[9.5px] font-bold uppercase tracking-wide" style={{ color: INK.primary }}>
              {d.label}
            </div>
            <div className="my-1 flex justify-center" style={{ color: meta.tone }}>
              <meta.Icon size={18} weight="duotone" />
            </div>
            <div className="text-[10px] tabular-nums font-bold leading-none" style={{ color: INK.primary }}>
              {formatTemp(d.max, tempUnit)}
            </div>
            <div className="relative my-1 h-1 rounded-full mx-1" style={{ background: `${INK.muted}20` }}>
              <div
                className="absolute h-full rounded-full"
                style={{
                  left: `${barLeft}%`,
                  width: `${Math.max(8, barWidth)}%`,
                  background: `linear-gradient(90deg, #60a5fa, ${meta.tone})`,
                }}
              />
            </div>
            <div className="text-[9.5px] tabular-nums leading-none" style={{ color: INK.muted }}>
              {formatTemp(d.min, tempUnit)}
            </div>
            {d.prob != null && d.prob >= 10 && (
              <div className="text-[8.5px] tabular-nums font-bold mt-0.5" style={{ color: "#0ea5e9" }}>
                {Math.round(d.prob)}%
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Sun arc ---------------- */

function SunArc({ sunrise, sunset, utcOffsetSeconds }) {
  if (!sunrise || !sunset) return null;
  const sr = parseLocalIso(sunrise, utcOffsetSeconds).getTime();
  const ss = parseLocalIso(sunset, utcOffsetSeconds).getTime();
  const now = Date.now();
  const total = ss - sr;
  const elapsed = now - sr;
  const pct = Math.max(0, Math.min(1, elapsed / total));
  const isDay = now >= sr && now <= ss;
  const W = 240;
  const H = 92;
  const cx = W / 2;
  const cy = H - 6;
  const r = (W - 24) / 2;
  const angle = Math.PI * (1 - pct);
  const sunX = cx + r * Math.cos(angle);
  const sunY = cy - r * Math.sin(angle);
  const daylightHours = total / 3600000;
  const remaining = Math.max(0, ss - now);
  const remH = Math.floor(remaining / 3600000);
  const remM = Math.floor((remaining % 3600000) / 60000);

  return (
    <div className="w-full" data-testid="sun-arc">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[92px] block">
        <defs>
          <linearGradient id="sun-arc-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="50%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <line x1={12} y1={cy} x2={W - 12} y2={cy} stroke={INK.rule} strokeWidth="1" />
        <path
          d={`M ${12},${cy} A ${r},${r} 0 0 1 ${W - 12},${cy}`}
          fill="none" stroke={INK.rule} strokeWidth="2" strokeDasharray="3,3"
        />
        {isDay && (
          <path
            d={`M ${12},${cy} A ${r},${r} 0 0 1 ${sunX.toFixed(1)},${sunY.toFixed(1)}`}
            fill="none" stroke="url(#sun-arc-grad)" strokeWidth="2.6" strokeLinecap="round"
          />
        )}
        <circle cx={12} cy={cy} r="3" fill="#f59e0b" />
        <text x={12} y={cy + 11} textAnchor="middle" fontSize="8" fontWeight="800" fill={INK.muted}>
          {fmtTime(sunrise).replace(" ", "").toLowerCase()}
        </text>
        <circle cx={W - 12} cy={cy} r="3" fill="#7c3aed" />
        <text x={W - 12} y={cy + 11} textAnchor="middle" fontSize="8" fontWeight="800" fill={INK.muted}>
          {fmtTime(sunset).replace(" ", "").toLowerCase()}
        </text>
        {isDay ? (
          <g>
            <circle cx={sunX} cy={sunY} r="9" fill="#fef3c7" />
            <circle cx={sunX} cy={sunY} r="6" fill="#f59e0b" />
          </g>
        ) : (
          <g>
            <circle cx={cx} cy={cy + 10} r="9" fill="#1e1b4b" opacity="0.18" />
            <Moon x={cx - 7} y={cy + 4} size={14} weight="fill" color="#7c3aed" />
          </g>
        )}
      </svg>
      <div className="grid grid-cols-3 gap-2 mt-1">
        <div>
          <div className="text-[9px] uppercase tracking-[0.16em] font-extrabold" style={{ color: INK.muted }}>Daylight</div>
          <div className="tabular-nums" style={{ ...NUM, fontSize: 14, color: INK.primary }}>
            {daylightHours.toFixed(1)}h
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.16em] font-extrabold" style={{ color: INK.muted }}>
            {isDay ? "Until dusk" : "Sun set"}
          </div>
          <div className="tabular-nums" style={{ ...NUM, fontSize: 14, color: INK.primary }}>
            {isDay ? `${remH}h ${remM}m` : "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-[0.16em] font-extrabold" style={{ color: INK.muted }}>Solar phase</div>
          <div className="tabular-nums" style={{ ...NUM, fontSize: 14, color: INK.primary }}>
            {Math.round(pct * 100)}%
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- AQI tile ---------------- */

function PollutantBar({ label, value, cap, unit, color }) {
  const pct = value != null ? Math.min(1, value / cap) : 0;
  const danger = pct >= 0.66;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-1 mb-0.5">
        <span className="text-[8.5px] uppercase tracking-[0.10em] font-bold" style={{ color: INK.muted }}>{label}</span>
        <span className="ml-auto text-[10.5px] font-extrabold tabular-nums" style={{ color: INK.primary }}>
          {value != null ? value.toFixed(0) : "—"}
          <span className="text-[8px] font-medium ml-0.5" style={{ color: INK.muted }}>{unit}</span>
        </span>
      </div>
      <div className="h-[6px] rounded-full overflow-hidden" style={{ background: `${color}24` }}>
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${Math.round(pct * 100)}%`, background: danger ? "#dc2626" : color }}
        />
      </div>
    </div>
  );
}

/* ---------------- UV through the day ---------------- */

function UvCurve({ points }) {
  if (!points || points.length < 6) return null;
  const W = 320;
  const H = 60;
  const PAD = 12;
  const peak = points.reduce((a, b) => (b.uv > a.uv ? b : a), points[0]);
  const step = (W - PAD * 2) / (points.length - 1);
  const pts = points.map((s, i) => {
    const x = PAD + i * step;
    const y = H - 8 - (Math.min(s.uv, 12) / 12) * (H - 18);
    return { x, y, ...s };
  });
  const path = pts.reduce((acc, p, i) => `${acc}${i === 0 ? "M" : " L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`, "");
  const area = `${path} L${pts[pts.length - 1].x},${H - 8} L${pts[0].x},${H - 8} Z`;
  const peakIdx = points.indexOf(peak);
  const peakPt = pts[peakIdx];
  const peakTone = UV_TONE(peak.uv);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[64px]" data-testid="uv-curve">
      <defs>
        <linearGradient id="uv-grad-v2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dc2626" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#fde047" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#uv-grad-v2)" />
      <path d={path} fill="none" stroke="#ea580c" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      {peakPt && peak.uv > 0 && (
        <g>
          <circle cx={peakPt.x} cy={peakPt.y} r="3.5" fill={peakTone.color} stroke="#fdfaf2" strokeWidth="1.5" />
          <text x={peakPt.x} y={peakPt.y - 6} textAnchor="middle" fontSize="9" fontWeight="800" fill={peakTone.color} style={NUM}>
            {Math.round(peak.uv)} peak
          </text>
        </g>
      )}
    </svg>
  );
}

/* ---------------- 30-day rainfall heatmap ---------------- */

function RainfallHeatmap({ days }) {
  if (!days || !days.length) return null;
  const max = Math.max(0.5, ...days.map((d) => d.mm));
  const cellTone = (mm) => {
    if (mm < 0.2) return "#e5e7eb";
    if (mm < 1) return "#bae6fd";
    if (mm < 5) return "#7dd3fc";
    if (mm < 15) return "#38bdf8";
    if (mm < 30) return "#0284c7";
    return "#1d4ed8";
  };
  // Group into rows of 6 for a 5-row x 6-col heatmap (30 days)
  const rows = [];
  for (let i = 0; i < days.length; i += 6) rows.push(days.slice(i, i + 6));

  return (
    <div className="w-full" data-testid="rainfall-heatmap">
      <div className="grid grid-cols-6 gap-1.5">
        {days.map((d, i) => {
          const tone = cellTone(d.mm);
          const intensity = Math.min(1, d.mm / max);
          const dayLabel = d.date?.slice(8, 10) || (i + 1);
          return (
            <div key={d.date || i} className="relative group">
              <div
                className="aspect-square rounded-[5px] flex items-center justify-center text-[8.5px] font-bold tabular-nums transition-transform"
                style={{
                  background: tone,
                  opacity: 0.45 + intensity * 0.55,
                  color: d.mm >= 5 ? "#fff" : "#1c1917",
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
                title={`${d.date} · ${d.mm.toFixed(1)} mm`}
              >
                {dayLabel}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="text-[8.5px] uppercase tracking-wider font-bold" style={{ color: INK.muted }}>Dry</span>
        <div className="flex-1 flex gap-0.5 mx-2">
          {[0, 0.5, 3, 10, 22, 40].map((v) => (
            <div key={v} className="flex-1 h-1.5 rounded-sm" style={{ background: cellTone(v) }} />
          ))}
        </div>
        <span className="text-[8.5px] uppercase tracking-wider font-bold" style={{ color: INK.muted }}>Heavy</span>
      </div>
    </div>
  );
}

/* ============================================================
 * MAIN PAGE
 * ============================================================ */

export default function ReportPage({ location, forecast, theme, active }) {
  const [aqi, setAqi] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [bundleStatus, setBundleStatus] = useState("idle");
  const { settings } = useSettings();
  const tempUnit = settings.tempUnit;

  const cur = forecast?.current || {};
  const daily = forecast?.daily || {};
  const hourly = forecast?.hourly || {};

  useEffect(() => {
    if (!active || !location?.latitude || !location?.longitude) return;
    let cancelled = false;
    setBundle(null);
    setBundleStatus("loading");
    getAirQuality(location.latitude, location.longitude)
      .then((r) => !cancelled && setAqi(r))
      .catch(() => {});
    getReportBundle(location.latitude, location.longitude)
      .then((r) => {
        if (cancelled) return;
        setBundle(r);
        setBundleStatus("ready");
      })
      .catch(() => !cancelled && setBundleStatus("error"));
    return () => { cancelled = true; };
  }, [active, location?.latitude, location?.longitude]);

  const hi = daily.temperature_2m_max?.[0];
  const lo = daily.temperature_2m_min?.[0];
  const sunrise = daily.sunrise?.[0];
  const sunset = daily.sunset?.[0];
  const utcOffsetSeconds = forecast?.utc_offset_seconds;
  const todayCode = daily.weather_code?.[0];
  const codeMeta = weatherCodeMeta(todayCode);

  // UV — fall back to "night" if all hours are 0.
  const uvData = bundle?.uv;
  const uvAllZero = uvData && uvData.points && uvData.points.every((p) => p.uv === 0);
  const isNightNow = uvAllZero || (uvData && uvData.peak_uv === 0);
  const uvTone = UV_TONE(uvData?.peak_uv);

  const allergy = allergyFromPm25(aqi?.pm2_5);
  const rain30 = bundle?.rain_30d;
  const lightning = bundle?.lightning_24h;
  const soil = bundle?.soil;
  const cool = bundle?.cool_zone;

  return (
    <div
      className="h-full w-full flex flex-col px-4 pt-3 pb-20"
      data-testid="page-report"
      style={{ color: INK.primary }}
    >
      {/* Masthead */}
      <div className="shrink-0 px-1 mb-3">
        <div className="text-[9px] uppercase tracking-[0.32em] font-extrabold" style={{ color: INK.muted }}>
          {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
        </div>
        <h2
          style={{ ...NUM, fontSize: 24, color: INK.primary }}
          className="leading-[1.05] mt-0.5"
          data-testid="report-title"
        >
          Detailed report
        </h2>
        {location?.name && (
          <div className="flex items-center gap-1 mt-1 text-[10.5px] font-medium" style={{ color: INK.secondary }}>
            <MapPin size={11} weight="fill" color={theme?.accent || INK.muted} />
            <span className="truncate">{location.name}{location.admin1 ? ` · ${location.admin1}` : ""}</span>
          </div>
        )}
      </div>

      {/* Scrolling body */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-1 pb-4 space-y-3"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        data-testid="report-scroll-body"
      >
        {/* Today's outlook hero */}
        <Section
          title="Today's outlook"
          accent={ACCENT.hi}
          right={
            <div className="flex items-center gap-1.5">
              <codeMeta.Icon size={14} weight="duotone" color={codeMeta.tone} />
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: INK.secondary }}>
                {codeMeta.word}
              </span>
            </div>
          }
          testId="report-day-arc"
          delay={0}
        >
          <div className="flex items-end gap-3 mb-2">
            <span className="leading-[0.85] tabular-nums" style={{ ...NUM, fontSize: 48, color: INK.primary }}>
              {formatTemp(cur.temperature_2m, tempUnit, { suffix: "" })}°
            </span>
            <div className="pb-1">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: INK.muted }}>Feels like</div>
              <div className="tabular-nums" style={{ ...NUM, fontSize: 18, color: INK.secondary }}>
                {formatTemp(cur.apparent_temperature, tempUnit)}
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: INK.muted }}>Today</div>
              <div className="text-[14px] font-extrabold tabular-nums" style={{ color: INK.primary }}>
                <span style={{ color: "#b91c1c" }}>{formatTemp(hi, tempUnit)}</span>
                <span className="mx-1" style={{ color: INK.muted }}>/</span>
                <span style={{ color: "#1e40af" }}>{formatTemp(lo, tempUnit)}</span>
              </div>
            </div>
          </div>
          <DayTempCurve hourly={hourly} current={cur} sunrise={sunrise} sunset={sunset} tempUnit={tempUnit} />
        </Section>

        {/* 7-day strip */}
        <Section title="Next 7 days" accent="#0ea5e9" testId="report-7day" delay={0.04}>
          <SevenDayStrip daily={daily} tempUnit={tempUnit} />
        </Section>

        {/* Sun arc */}
        <Section title="Sun · Today" accent={ACCENT.sun} testId="report-sun" delay={0.06}>
          <SunArc sunrise={sunrise} sunset={sunset} utcOffsetSeconds={utcOffsetSeconds} />
        </Section>

        {/* Air quality */}
        <Section
          title="Air quality"
          accent={aqi?.color || "#ca8a04"}
          right={
            aqi?.label ? (
              <Pill color={aqi.color} testId="aqi-label">
                {String(aqi.label).replace("-sg", " · sensitive").replace("-", " ")}
              </Pill>
            ) : null
          }
          testId="report-aqi"
          delay={0.08}
        >
          <div className="flex items-end gap-3">
            <div className="flex items-baseline gap-1 shrink-0">
              <span className="tabular-nums leading-none" style={{ ...NUM, fontSize: 38, color: aqi?.color || INK.primary }} data-testid="aqi-value">
                {aqi?.us_aqi != null ? Math.round(aqi.us_aqi) : "—"}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: INK.muted }}>AQI</span>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-2.5 min-w-0">
              <PollutantBar label="PM2.5" value={aqi?.pm2_5} cap={35} unit="µg" color={aqi?.color || INK.muted} />
              <PollutantBar label="PM10" value={aqi?.pm10} cap={150} unit="µg" color={aqi?.color || INK.muted} />
              <PollutantBar label="O₃" value={aqi?.ozone} cap={180} unit="µg" color={aqi?.color || INK.muted} />
            </div>
          </div>
          {aqi?.advice && (
            <p className="text-[11px] mt-3 leading-snug font-medium" style={{ color: INK.secondary }}>
              {aqi.advice}
            </p>
          )}
        </Section>

        {/* Allergy / Pollen proxy */}
        {allergy && (
          <Section
            title="Allergy index"
            accent={allergy.color}
            right={<Pill color={allergy.color} testId="allergy-label">{allergy.level}</Pill>}
            testId="report-allergy"
            delay={0.10}
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 rounded-full flex items-center justify-center"
                style={{ width: 52, height: 52, background: `${allergy.color}1f`, border: `1.5px solid ${allergy.color}55` }}
              >
                <Leaf size={24} weight="duotone" color={allergy.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-extrabold leading-tight" style={{ color: INK.primary }}>
                  {allergy.advice}
                </div>
                <div className="text-[10px] mt-1 leading-snug" style={{ color: INK.muted }}>
                  Estimated from PM2.5 ({aqi?.pm2_5?.toFixed(0) ?? "—"} µg/m³). Higher particulate loads track airborne irritants.
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* UV through the day */}
        <Section
          title="UV through the day"
          accent={isNightNow ? ACCENT.moon : uvTone.color}
          right={
            isNightNow ? (
              <Pill color={ACCENT.moon} testId="uv-label">Night</Pill>
            ) : (
              <Pill color={uvTone.color} testId="uv-label">{uvTone.label}</Pill>
            )
          }
          testId="report-uv"
          delay={0.12}
        >
          {isNightNow ? (
            <div className="flex items-center gap-3 py-2" data-testid="uv-night-fallback">
              <div
                className="shrink-0 rounded-full flex items-center justify-center"
                style={{ width: 52, height: 52, background: `${ACCENT.moon}1f`, border: `1.5px solid ${ACCENT.moon}55` }}
              >
                <Moon size={26} weight="duotone" color={ACCENT.moon} />
              </div>
              <div>
                <div className="text-[16px] font-extrabold leading-tight" style={{ color: INK.primary }}>
                  UV is 0 right now — the sun has set.
                </div>
                <div className="text-[10.5px] mt-1 font-medium" style={{ color: INK.muted }}>
                  Skin protection isn't needed at night. Check back after sunrise.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3 mb-1.5">
                <span className="tabular-nums leading-none" style={{ ...NUM, fontSize: 30, color: uvTone.color }}>
                  {uvData?.peak_uv != null ? Math.round(uvData.peak_uv) : "—"}
                </span>
                <div className="pb-1">
                  <div className="text-[9.5px] uppercase tracking-[0.16em] font-extrabold" style={{ color: INK.muted }}>
                    Peak today
                  </div>
                  <div className="text-[11px] font-bold" style={{ color: INK.secondary }}>
                    {uvData?.peak_hour_label || "—"} · {uvTone.label}
                  </div>
                </div>
              </div>
              <UvCurve points={uvData?.points} />
              {uvData?.band?.advice && (
                <p className="text-[10.5px] mt-1.5 leading-snug font-medium" style={{ color: INK.secondary }}>
                  {uvData.band.advice}
                </p>
              )}
            </>
          )}
        </Section>

        {/* 30-day rainfall heatmap */}
        {rain30 && rain30.days?.length > 0 && (
          <Section
            title="Rainfall · last 30 days"
            accent={ACCENT.rain}
            right={
              rain30.pct_of_normal != null ? (
                <Pill
                  color={rain30.pct_of_normal >= 130 ? "#1d4ed8" : rain30.pct_of_normal >= 85 ? "#16a34a" : "#d97706"}
                  testId="rain30-pct"
                >
                  {rain30.pct_of_normal}% of normal
                </Pill>
              ) : null
            }
            testId="report-rain-30d"
            delay={0.14}
          >
            <div className="flex items-end gap-3 mb-3">
              <span className="tabular-nums leading-none" style={{ ...NUM, fontSize: 30, color: ACCENT.rain }}>
                {rain30.total_mm?.toFixed(0) ?? "0"}
              </span>
              <div className="pb-1">
                <div className="text-[9.5px] uppercase tracking-[0.16em] font-extrabold" style={{ color: INK.muted }}>mm fell here</div>
                {rain30.normal_mm != null && (
                  <div className="text-[11px] font-bold" style={{ color: INK.secondary }}>
                    Normal {rain30.normal_mm?.toFixed(0)} mm
                  </div>
                )}
              </div>
            </div>
            <RainfallHeatmap days={rain30.days} />
            {rain30.verdict && (
              <p className="text-[11px] mt-3 leading-snug font-medium" style={{ color: INK.secondary }} data-testid="rain30-verdict">
                {rain30.verdict}
              </p>
            )}
          </Section>
        )}

        {/* 24h Lightning */}
        {lightning && (
          <Section
            title="Lightning · 24 h nearby"
            accent={ACCENT.bolt}
            right={
              <Pill color={ACCENT.bolt} testId="lightning-count">
                {lightning.count_24h ?? 0} {lightning.count_24h === 1 ? "strike" : "strikes"}
              </Pill>
            }
            testId="report-lightning"
            delay={0.16}
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 rounded-full flex items-center justify-center"
                style={{
                  width: 52,
                  height: 52,
                  background: `${ACCENT.bolt}1f`,
                  border: `1.5px solid ${ACCENT.bolt}55`,
                }}
              >
                <Lightning size={26} weight="fill" color={ACCENT.bolt} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-extrabold leading-tight" style={{ color: INK.primary }}>
                  {lightning.verdict}
                </div>
                <div className="text-[10px] mt-1 leading-snug font-medium" style={{ color: INK.muted }}>
                  {lightning.closest_km != null
                    ? `Closest strike: ${lightning.closest_km} km away · `
                    : ""}
                  Within {lightning.radius_km} km radius. Source: Weatherbit.
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Soil moisture */}
        {soil && soil.topsoil_pct != null && (
          <Section
            title="Soil moisture"
            accent={ACCENT.soil}
            testId="report-soil"
            delay={0.18}
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 rounded-full flex items-center justify-center"
                style={{ width: 52, height: 52, background: `${ACCENT.soil}1f`, border: `1.5px solid ${ACCENT.soil}55` }}
              >
                <Drop size={24} weight="duotone" color={ACCENT.soil} />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="tabular-nums" style={{ ...NUM, fontSize: 24, color: INK.primary }}>
                    {soil.topsoil_pct.toFixed(1)}%
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: INK.muted }}>topsoil</span>
                  {soil.rootzone_pct != null && (
                    <span className="ml-auto text-[10px] font-bold tabular-nums" style={{ color: INK.muted }}>
                      Rootzone {soil.rootzone_pct.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: `${ACCENT.soil}1f` }}>
                  <div
                    className="h-full rounded-full transition-[width] duration-700"
                    style={{ width: `${Math.min(100, soil.topsoil_pct * 2)}%`, background: ACCENT.soil }}
                  />
                </div>
                {soil.advice && (
                  <p className="text-[11px] leading-snug font-medium" style={{ color: INK.secondary }}>
                    {soil.advice}
                  </p>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* Cool-zone travel suggestion */}
        {cool && cool.available && (
          <Section
            title="Cool getaway nearby"
            accent={ACCENT.cool}
            right={<Pill color={ACCENT.cool}>{cool.distance_km} km</Pill>}
            testId="report-cool-zone"
            delay={0.20}
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 rounded-full flex items-center justify-center"
                style={{ width: 52, height: 52, background: `${ACCENT.cool}1f`, border: `1.5px solid ${ACCENT.cool}55` }}
              >
                <Wind size={26} weight="duotone" color={ACCENT.cool} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[16px] font-extrabold leading-tight" style={{ color: INK.primary }}>
                    {cool.city}
                  </span>
                  {cool.state && (
                    <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: INK.muted }}>
                      {cool.state}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="tabular-nums" style={{ ...NUM, fontSize: 22, color: ACCENT.cool }}>
                    {formatTemp(cool.temp_c, tempUnit)}
                  </span>
                  <span className="text-[11px] font-bold" style={{ color: INK.secondary }}>
                    {cool.delta_c}° cooler than here
                  </span>
                </div>
                <p className="text-[10.5px] mt-1.5 leading-snug font-medium" style={{ color: INK.muted }}>
                  Within driving distance — perfect for an escape from the heat.
                </p>
              </div>
            </div>
          </Section>
        )}

        {/* Loading / error footers */}
        {bundleStatus === "loading" && !bundle && (
          <div className="text-center text-[11px] py-3 font-medium" style={{ color: INK.muted }} data-testid="report-bundle-loading">
            Loading deeper insights…
          </div>
        )}
        {bundleStatus === "error" && (
          <div className="text-center text-[11px] py-3 font-medium" style={{ color: INK.muted }} data-testid="report-bundle-error">
            Some advanced tiles couldn't load. Pull down to retry.
          </div>
        )}
      </div>
    </div>
  );
}
